"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SettingsManager = void 0;
const viewer_shared_services_1 = require("@shapediver/viewer.shared.services");
class SettingsManager {
    constructor(renderingEngine, props) {
        this._converter = viewer_shared_services_1.Converter.instance;
        this._eventEngine = viewer_shared_services_1.EventEngine.instance;
        this._stateEngine = viewer_shared_services_1.StateEngine.instance;
        this._renderingEngine = renderingEngine;
        this._sessionSettingsMode = props.sessionSettingsMode;
        this._sessionSettingsId = props.sessionSettingsId;
    }
    get sessionSettingsId() {
        return this._sessionSettingsId;
    }
    set sessionSettingsId(value) {
        this._sessionSettingsId = value;
    }
    get sessionSettingsMode() {
        return this._sessionSettingsMode;
    }
    set sessionSettingsMode(value) {
        this._sessionSettingsMode = value;
    }
    get settingsEngine() {
        return this._settingsEngine;
    }
    set settingsEngine(value) {
        this._settingsEngine = value;
    }
    applySettings(sections = {
        ar: true,
        scene: true,
        camera: true,
        light: true,
        environment: true,
        general: true,
        postprocessing: true,
    }, settingsEngine, updateViewport = true) {
        return __awaiter(this, void 0, void 0, function* () {
            settingsEngine = settingsEngine || this._settingsEngine;
            if (!settingsEngine)
                return;
            if (sections.environment) {
                const promises = [];
                promises.push(this._renderingEngine.postProcessingManager.initialize());
                // as the environment map is the only thing that needs time to load, load it first
                promises.push(new Promise((resolve, reject) => {
                    var _a;
                    (_a = this._stateEngine.viewportEngines[this._renderingEngine.id]) === null || _a === void 0 ? void 0 : _a.environmentMapLoaded.then(() => {
                        try {
                            if (!settingsEngine)
                                return;
                            this._renderingEngine.environmentMapAsBackground =
                                settingsEngine.environment.mapAsBackground;
                            this._renderingEngine.clearAlpha =
                                settingsEngine.environment.clearAlpha;
                            this._renderingEngine.clearColor =
                                this._converter.toHexColor(settingsEngine.environment.clearColor);
                            this._renderingEngine.environmentMapRotation = [
                                settingsEngine.environment.rotation.x,
                                settingsEngine.environment.rotation.y,
                                settingsEngine.environment.rotation.z,
                                settingsEngine.environment.rotation.w,
                            ];
                            this._renderingEngine.environmentMapBlurriness =
                                settingsEngine.environment.blurriness;
                            this._renderingEngine.environmentMapIntensity =
                                settingsEngine.environment.intensity;
                            this.applySyncSettings(sections, settingsEngine, updateViewport);
                            this._eventEngine.emitEvent(viewer_shared_services_1.EVENTTYPE_VIEWPORT.VIEWPORT_SETTINGS_LOADED, {
                                viewportId: this._renderingEngine.id,
                            });
                            resolve();
                        }
                        catch (e) {
                            reject(e);
                        }
                    }).catch((e) => reject(e));
                    // set it like this to not trigger the loading
                    this._renderingEngine.environmentMap =
                        this._renderingEngine.environmentMapLoader.reconstructSavedEnvironmentMapContent(settingsEngine.environment.map);
                }));
                yield Promise.all(promises);
            }
            else {
                this.applySyncSettings(sections, settingsEngine, updateViewport);
                this._eventEngine.emitEvent(viewer_shared_services_1.EVENTTYPE_VIEWPORT.VIEWPORT_SETTINGS_LOADED, { viewportId: this._renderingEngine.id });
            }
        });
    }
    assignSettingsEngine(settingsEngine) {
        this._settingsEngine = settingsEngine;
    }
    init() { }
    saveSettings(settingsEngine) {
        settingsEngine = settingsEngine || this._settingsEngine;
        if (!settingsEngine)
            return;
        this._renderingEngine.lightEngine.saveSettings(settingsEngine);
        this._renderingEngine.cameraEngine.saveSettings(settingsEngine);
        (this._renderingEngine.postProcessingManager).saveSettings(settingsEngine);
        settingsEngine.ar.enable = this._renderingEngine.enableAR;
        settingsEngine.environment.mapResolution =
            this._renderingEngine.environmentMapResolution;
        settingsEngine.environment.map =
            this._renderingEngine.environmentMapLoader.createSaveableEnvironmentMapContent(this._renderingEngine.environmentMap);
        settingsEngine.environment.mapAsBackground =
            this._renderingEngine.environmentMapAsBackground;
        settingsEngine.environment.clearAlpha =
            this._renderingEngine.clearAlpha;
        settingsEngine.environment.clearColor = this._converter.toHexColor(this._renderingEngine.clearColor);
        settingsEngine.environment.rotation = {
            x: this._renderingEngine.environmentMapRotation[0],
            y: this._renderingEngine.environmentMapRotation[1],
            z: this._renderingEngine.environmentMapRotation[2],
            w: this._renderingEngine.environmentMapRotation[3],
        };
        settingsEngine.environment.blurriness =
            this._renderingEngine.environmentMapBlurriness;
        settingsEngine.environment.intensity =
            this._renderingEngine.environmentMapIntensity;
        settingsEngine.environmentGeometry.gridVisibility =
            this._renderingEngine.gridVisibility;
        settingsEngine.environmentGeometry.groundPlaneVisibility =
            this._renderingEngine.groundPlaneVisibility;
        settingsEngine.environmentGeometry.groundPlaneShadowVisibility =
            this._renderingEngine.groundPlaneShadowVisibility;
        settingsEngine.environmentGeometry.gridColor =
            this._converter.toHexColor(this._renderingEngine.gridColor);
        settingsEngine.environmentGeometry.groundPlaneColor =
            this._converter.toHexColor(this._renderingEngine.groundPlaneColor);
        settingsEngine.environmentGeometry.groundPlaneShadowColor =
            this._converter.toHexColor(this._renderingEngine.groundPlaneShadowColor);
        settingsEngine.environmentGeometry.contactShadowBlur =
            this._renderingEngine.contactShadowBlur;
        settingsEngine.environmentGeometry.contactShadowDarkness =
            this._renderingEngine.contactShadowDarkness;
        settingsEngine.environmentGeometry.contactShadowHeight =
            this._renderingEngine.contactShadowHeight;
        settingsEngine.environmentGeometry.contactShadowOpacity =
            this._renderingEngine.contactShadowOpacity;
        settingsEngine.environmentGeometry.contactShadowVisibility =
            this._renderingEngine.contactShadowVisibility;
        settingsEngine.general.pointSize = this._renderingEngine.pointSize;
        settingsEngine.general.transformation.rotation = {
            x: this._renderingEngine.arRotation[0],
            y: this._renderingEngine.arRotation[1],
            z: this._renderingEngine.arRotation[2],
        };
        settingsEngine.general.transformation.translation = {
            x: this._renderingEngine.arTranslation[0],
            y: this._renderingEngine.arTranslation[1],
            z: this._renderingEngine.arTranslation[2],
        };
        settingsEngine.general.transformation.scale = {
            x: this._renderingEngine.arScale[0],
            y: this._renderingEngine.arScale[1],
            z: this._renderingEngine.arScale[2],
        };
        settingsEngine.material.defaultMaterialColor =
            this._converter.toHexColor(this._renderingEngine.defaultMaterialColor);
        settingsEngine.material.materialOverrideType =
            this._renderingEngine.materialOverrideType;
        settingsEngine.rendering.automaticColorAdjustment =
            this._renderingEngine.automaticColorAdjustment;
        settingsEngine.rendering.lights = this._renderingEngine.lights;
        settingsEngine.rendering.outputEncoding =
            this._renderingEngine.outputEncoding;
        settingsEngine.rendering.physicallyCorrectLights =
            this._renderingEngine.physicallyCorrectLights;
        settingsEngine.rendering.textureEncoding =
            this._renderingEngine.textureEncoding;
        settingsEngine.rendering.toneMapping =
            this._renderingEngine.toneMapping;
        settingsEngine.rendering.toneMappingExposure =
            this._renderingEngine.toneMappingExposure;
        settingsEngine.rendering.beautyRenderBlendingDuration =
            this._renderingEngine.beautyRenderBlendingDuration;
        settingsEngine.rendering.beautyRenderDelay =
            this._renderingEngine.beautyRenderDelay;
        settingsEngine.rendering.shadows = this._renderingEngine.shadows;
        settingsEngine.rendering.softShadows =
            this._renderingEngine.softShadows;
    }
    applySyncSettings(sections = {
        ar: true,
        scene: true,
        camera: true,
        light: true,
        environment: true,
        general: true,
        postprocessing: true,
    }, settingsEngine, updateViewport = true) {
        var _a;
        settingsEngine = settingsEngine || this._settingsEngine;
        if (!settingsEngine)
            return;
        if (sections.ar) {
            this._renderingEngine.enableAR = settingsEngine.ar.enable;
            this._renderingEngine.arScale = [
                settingsEngine.general.transformation.scale.x,
                settingsEngine.general.transformation.scale.y,
                settingsEngine.general.transformation.scale.z,
            ];
            this._renderingEngine.arTranslation = [
                settingsEngine.general.transformation.translation.x,
                settingsEngine.general.transformation.translation.y,
                settingsEngine.general.transformation.translation.z,
            ];
            this._renderingEngine.arRotation = [
                settingsEngine.general.transformation.rotation.x,
                settingsEngine.general.transformation.rotation.y,
                settingsEngine.general.transformation.rotation.z,
            ];
        }
        if (sections.scene) {
            this._renderingEngine.gridColor =
                settingsEngine.environmentGeometry.gridColor;
            this._renderingEngine.gridVisibility =
                settingsEngine.environmentGeometry.gridVisibility;
            this._renderingEngine.groundPlaneColor =
                settingsEngine.environmentGeometry.groundPlaneColor;
            this._renderingEngine.groundPlaneVisibility =
                settingsEngine.environmentGeometry.groundPlaneVisibility;
            this._renderingEngine.groundPlaneShadowColor =
                settingsEngine.environmentGeometry.groundPlaneShadowColor;
            this._renderingEngine.groundPlaneShadowVisibility =
                settingsEngine.environmentGeometry.groundPlaneShadowVisibility;
            this._renderingEngine.contactShadowBlur =
                settingsEngine.environmentGeometry.contactShadowBlur;
            this._renderingEngine.contactShadowDarkness =
                settingsEngine.environmentGeometry.contactShadowDarkness;
            this._renderingEngine.contactShadowHeight =
                settingsEngine.environmentGeometry.contactShadowHeight;
            this._renderingEngine.contactShadowOpacity =
                settingsEngine.environmentGeometry.contactShadowOpacity;
            this._renderingEngine.contactShadowVisibility =
                settingsEngine.environmentGeometry.contactShadowVisibility;
            this._renderingEngine.shadows = settingsEngine.rendering.shadows;
            this._renderingEngine.softShadows =
                settingsEngine.rendering.softShadows;
            this._renderingEngine.lights = settingsEngine.rendering.lights;
            this._renderingEngine.automaticColorAdjustment =
                settingsEngine.rendering.automaticColorAdjustment;
            this._renderingEngine.textureEncoding = (settingsEngine.rendering.textureEncoding);
            this._renderingEngine.outputEncoding = (settingsEngine.rendering.outputEncoding);
            this._renderingEngine.physicallyCorrectLights =
                settingsEngine.rendering.physicallyCorrectLights;
            this._renderingEngine.toneMapping = (settingsEngine.rendering.toneMapping);
            this._renderingEngine.toneMappingExposure =
                settingsEngine.rendering.toneMappingExposure;
        }
        if (sections.general) {
            this._renderingEngine.defaultMaterialColor =
                settingsEngine.material.defaultMaterialColor;
            this._renderingEngine.materialOverrideType = settingsEngine.material
                .materialOverrideType;
            this._renderingEngine.pointSize = settingsEngine.general.pointSize;
        }
        if (sections.light)
            this._renderingEngine.lightEngine.applySettings(settingsEngine);
        if (sections.camera)
            this._renderingEngine.cameraEngine.applySettings(settingsEngine);
        if (sections.postprocessing)
            (this._renderingEngine.postProcessingManager).applySettings(settingsEngine);
        // call adjust camera to load the three.js camera objects
        this._renderingEngine.cameraManager.adjustCamera(1);
        (_a = this._stateEngine.viewportEngines[this._renderingEngine.id]) === null || _a === void 0 ? void 0 : _a.settingsAssigned.resolve(true);
        if (updateViewport)
            this._renderingEngine.update("RenderingEngine.applySyncSettings");
    }
}
exports.SettingsManager = SettingsManager;
//# sourceMappingURL=SettingsManager.js.map